import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import { incrementVibeAccuracyForDailyCheck } from './vibeAccuracy';

export const VIBE_CHECK_STREAK_MAX = 5;

/** Matches from the daily check and "GET A NEW MATCH" allowed in one 3am period. */
export const DAILY_MATCH_LIMIT = 3;

function normalizeMatchConversationIds(ids) {
  if (!Array.isArray(ids)) return [];
  const out = [];
  ids.forEach((id) => {
    if (typeof id === 'string' && id && !out.includes(id)) out.push(id);
  });
  return out;
}

/** Deterministic doc id for a daily-vibe-share pair (sorted uid join). Must match firestore.rules. */
export function getDailyVibeShareId(uidA, uidB) {
  return uidA < uidB ? `${uidA}_${uidB}` : `${uidB}_${uidA}`;
}

/**
 * Inviter creates (or re-opens) a pending share invite for a pair. Idempotent:
 * re-inviting after a decline resets the doc back to pending.
 */
export async function createDailyVibeShareInvite(firestore, inviterUid, otherUid) {
  if (!inviterUid || !otherUid || inviterUid === otherUid) return null;
  const shareId = getDailyVibeShareId(inviterUid, otherUid);
  const ref = doc(firestore, 'dailyVibeShares', shareId);
  await setDoc(
    ref,
    {
      participants: [inviterUid, otherUid].sort(),
      inviterUid,
      status: 'pending',
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
  return shareId;
}

/** Recipient (or either participant) sets the share status to accepted/declined. */
export async function setDailyVibeShareStatus(firestore, uidA, uidB, status) {
  if (!uidA || !uidB) return;
  const shareId = getDailyVibeShareId(uidA, uidB);
  const ref = doc(firestore, 'dailyVibeShares', shareId);
  await updateDoc(ref, { status, updatedAt: serverTimestamp() });
}

export async function acceptDailyVibeShare(firestore, uidA, uidB) {
  return setDailyVibeShareStatus(firestore, uidA, uidB, 'accepted');
}

export async function declineDailyVibeShare(firestore, uidA, uidB) {
  return setDailyVibeShareStatus(firestore, uidA, uidB, 'declined');
}

/** Live status of the share between two users (null when no doc exists). */
export function subscribeToDailyVibeShare(firestore, uidA, uidB, callback) {
  if (!uidA || !uidB) {
    callback(null);
    return () => {};
  }
  const shareId = getDailyVibeShareId(uidA, uidB);
  const ref = doc(firestore, 'dailyVibeShares', shareId);
  return onSnapshot(
    ref,
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    () => callback(null),
  );
}

/**
 * Live subscription to ALL of the user's accepted vibe-share partners.
 * Emits an array of otherUids ordered most-recently-accepted first.
 */
export function subscribeToAcceptedDailyVibeShares(firestore, uid, callback) {
  if (!uid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, 'dailyVibeShares'),
    where('participants', 'array-contains', uid),
  );
  return onSnapshot(
    q,
    (snap) => {
      callback(acceptedPartnerUidsFromShareSnap(snap, uid));
    },
    () => callback([]),
  );
}

/** One-shot list of accepted vibe-share partner uids (most recent first). */
export async function loadAcceptedDailyVibePartnerUids(firestore, uid) {
  if (!uid) return [];
  const q = query(
    collection(firestore, 'dailyVibeShares'),
    where('participants', 'array-contains', uid),
  );
  try {
    const snap = await getDocs(q);
    return acceptedPartnerUidsFromShareSnap(snap, uid);
  } catch {
    return [];
  }
}

function acceptedPartnerUidsFromShareSnap(snap, uid) {
  const rows = [];
  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.status !== 'accepted') return;
    const otherUid = Array.isArray(data.participants)
      ? data.participants.find((p) => p !== uid)
      : null;
    if (!otherUid) return;
    rows.push({ otherUid, ts: data.updatedAt?.toMillis?.() ?? 0 });
  });
  rows.sort((a, b) => b.ts - a.ts);
  return rows.map((r) => r.otherUid);
}

/**
 * Live subscription to a single user's latest daily-check GIF for a period.
 * Emits `{ id, gifUrl, stillUrl, timestamp }` or null.
 */
export function subscribeToDailyVibeCheckForPeriod(
  firestore,
  userId,
  periodKey,
  callback,
) {
  if (!userId || !periodKey) {
    callback(null);
    return () => {};
  }
  const q = query(
    collection(firestore, 'dailyvibe', userId, 'checks'),
    where('periodKey', '==', periodKey),
  );
  return onSnapshot(
    q,
    (snap) => {
      let latest = null;
      let latestTs = -1;
      snap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (!data?.gifUrl) return;
        const ts = data.createdAt?.toMillis?.() ?? 0;
        if (ts >= latestTs) {
          latestTs = ts;
          latest = {
            id: docSnap.id,
            gifUrl: data.gifUrl,
            stillUrl: data.stillUrl || null,
            timestamp: ts || Date.now(),
          };
        }
      });
      callback(latest);
    },
    () => callback(null),
  );
}

/**
 * Live subscription to the user's most recently accepted vibe-share partner.
 * Emits { otherUid } or null.
 */
export function subscribeToAcceptedDailyVibeShare(firestore, uid, callback) {
  if (!uid) {
    callback(null);
    return () => {};
  }
  // Single array-contains filter avoids a composite index; status is filtered client-side.
  const q = query(
    collection(firestore, 'dailyVibeShares'),
    where('participants', 'array-contains', uid),
  );
  return onSnapshot(
    q,
    (snap) => {
      let best = null;
      let bestTs = -1;
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.status !== 'accepted') return;
        const ts = data.updatedAt?.toMillis?.() ?? 0;
        if (ts >= bestTs) {
          bestTs = ts;
          best = data;
        }
      });
      if (!best) {
        callback(null);
        return;
      }
      const otherUid = Array.isArray(best.participants)
        ? best.participants.find((p) => p !== uid)
        : null;
      callback(otherUid ? { otherUid } : null);
    },
    () => callback(null),
  );
}

/** Daily vibe “day” starts at 3:00 AM local time. */
export function getDailyVibePeriodStart(now = new Date()) {
  const periodStart = new Date(now);
  periodStart.setHours(3, 0, 0, 0);
  if (now < periodStart) {
    periodStart.setDate(periodStart.getDate() - 1);
  }
  return periodStart;
}

/** Next 3:00 AM local boundary — when the fox daily-check thread refreshes. */
export function getNextDailyVibePeriodStart(now = new Date()) {
  const next = getDailyVibePeriodStart(now);
  next.setDate(next.getDate() + 1);
  return next;
}

function formatDailyVibePeriodKeyFromDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Stable key for the current 3am–3am window (local calendar date of period start). */
export function getDailyVibePeriodKey(now = new Date()) {
  return formatDailyVibePeriodKeyFromDate(getDailyVibePeriodStart(now));
}

/** Period key for the prior 3am–3am window (yesterday's vibe-check day). */
export function getPreviousDailyVibePeriodKey(now = new Date()) {
  const previousStart = getDailyVibePeriodStart(now);
  previousStart.setDate(previousStart.getDate() - 1);
  return formatDailyVibePeriodKeyFromDate(previousStart);
}

/**
 * UI streak count (0–5). Alive through today or yesterday; 0 after a missed day.
 *
 * @param {number|null|undefined} storedStreak
 * @param {string|null|undefined} lastPeriodKey
 */
export function resolveVibeCheckStreakDisplay(storedStreak, lastPeriodKey) {
  const streak = typeof storedStreak === 'number' && Number.isFinite(storedStreak) && storedStreak >= 0
    ? Math.min(VIBE_CHECK_STREAK_MAX, Math.floor(storedStreak))
    : 0;

  if (!lastPeriodKey) return 0;

  const current = getDailyVibePeriodKey();
  const previous = getPreviousDailyVibePeriodKey();

  if (lastPeriodKey === current || lastPeriodKey === previous) {
    return streak;
  }

  return 0;
}

function dailyVibeCacheStorageKey(userId, periodKey) {
  return `dailyVibeChat:${userId}:${periodKey}`;
}

function readDailyVibePeriodCache(userId, periodKey = getDailyVibePeriodKey()) {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(dailyVibeCacheStorageKey(userId, periodKey));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/** Fast local restore for remounted FoxChat (Messages inbox re-entry). */
export function readDailyVibeChatCache(userId, periodKey = getDailyVibePeriodKey()) {
  const data = readDailyVibePeriodCache(userId, periodKey);
  if (!data?.gifUrl) return null;
  return data;
}

/** When the user first saw today's fox daily-check greeting (ms since epoch). */
export function readDailyVibeGreetingSeenAt(userId, periodKey = getDailyVibePeriodKey()) {
  const seenAt = readDailyVibePeriodCache(userId, periodKey)?.greetingSeenAt;
  return typeof seenAt === 'number' && Number.isFinite(seenAt) ? seenAt : null;
}

/** Persist first-seen time for the greeting; returns the stored ms timestamp. */
export function recordDailyVibeGreetingSeenAt(
  userId,
  periodKey = getDailyVibePeriodKey(),
  seenAt = Date.now(),
) {
  if (!userId || typeof window === 'undefined') return seenAt;
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey);
    if (existing?.greetingSeenAt) return existing.greetingSeenAt;
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({ ...(existing || {}), greetingSeenAt: seenAt, periodKey }),
    );
    return seenAt;
  } catch {
    return seenAt;
  }
}

/** Whether the user already sent a daily-check GIF this 3am period (local cache). */
export function hasDailyVibeCheckForPeriod(userId, periodKey = getDailyVibePeriodKey()) {
  return Boolean(readDailyVibeChatCache(userId, periodKey)?.gifUrl);
}

/** Whether the on-demand match engine already ran for this period (avoid repeat scans). */
export function readDailyVibeMatchEngineRan(userId, periodKey = getDailyVibePeriodKey()) {
  return Boolean(readDailyVibePeriodCache(userId, periodKey)?.matchEngineRanAt);
}

export function recordDailyVibeMatchEngineRan(userId, periodKey = getDailyVibePeriodKey()) {
  if (!userId || typeof window === 'undefined') return;
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey) || {};
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({ ...existing, matchEngineRanAt: Date.now(), periodKey }),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Conversation initiated from this period's daily-check match, if any. */
export function readDailyVibeMatchFound(userId, periodKey = getDailyVibePeriodKey()) {
  const data = readDailyVibePeriodCache(userId, periodKey);
  if (!data?.matchFoundConversationId) return null;
  return {
    conversationId: data.matchFoundConversationId,
    foundAt: typeof data.matchFoundAt === 'number' && Number.isFinite(data.matchFoundAt)
      ? data.matchFoundAt
      : null,
  };
}

/**
 * Terminal fox daily-check reply for this period (an eligibility gate or a
 * "no matches" result). Persisted locally so it survives re-opens and fresh
 * FoxChat mounts (e.g. opening the chat from the Messages tab), the same way
 * the "match found" reply is restored.
 *
 * @returns {{ type: 'gate', gate: string, at: number|null } | { type: 'noMatches', at: number|null } | null}
 */
export function readDailyVibeCheckOutcome(userId, periodKey = getDailyVibePeriodKey()) {
  const outcome = readDailyVibePeriodCache(userId, periodKey)?.checkOutcome;
  if (!outcome || typeof outcome !== 'object' || !outcome.type) return null;
  const at = typeof outcome.at === 'number' && Number.isFinite(outcome.at) ? outcome.at : null;
  if (outcome.type === 'gate') {
    if (!outcome.gate) return null;
    return { type: 'gate', gate: outcome.gate, at };
  }
  if (outcome.type === 'noMatches') {
    return { type: 'noMatches', at };
  }
  return null;
}

/** Persist the terminal daily-check reply (gate or no-matches) for this period. */
export function recordDailyVibeCheckOutcome(userId, outcome, periodKey = getDailyVibePeriodKey()) {
  if (!userId || !outcome?.type || typeof window === 'undefined') return;
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey) || {};
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({
        ...existing,
        checkOutcome: { ...outcome, at: outcome.at || Date.now() },
        periodKey,
      }),
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * Drop this period's cached terminal daily-check reply (gate or no-matches) so the
 * thread is recomputed from scratch — used when an eligibility gate clears mid-period
 * (e.g. the user gets verified) and we want the match check to run again.
 */
export function clearDailyVibeCheckOutcome(userId, periodKey = getDailyVibePeriodKey()) {
  if (!userId || typeof window === 'undefined') return;
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey);
    if (!existing || !('checkOutcome' in existing)) return;
    const { checkOutcome, ...rest } = existing;
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({ ...rest, periodKey }),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Drop this period's cached latest match so a fresh search can replace it. The day's id list stays. */
export function clearDailyVibeMatchFound(userId, periodKey = getDailyVibePeriodKey()) {
  if (!userId || typeof window === 'undefined') return;
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey);
    if (!existing || !existing.matchFoundConversationId) return;
    const rest = { ...existing };
    delete rest.matchFoundConversationId;
    delete rest.matchFoundAt;
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({ ...rest, periodKey }),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Conversation ids matched this period (local cache), latest last. */
export function readDailyVibeMatchIds(userId, periodKey = getDailyVibePeriodKey()) {
  const data = readDailyVibePeriodCache(userId, periodKey);
  const ids = normalizeMatchConversationIds(data?.matchConversationIds);
  const single = data?.matchFoundConversationId;
  if (typeof single === 'string' && single && !ids.includes(single)) ids.push(single);
  return ids;
}

/**
 * Merge server- and client-known match conversations for this period.
 * Keeps ids the other side hasn't seen yet (another device, or a stamp still in flight).
 */
export function syncDailyVibeMatchIds(userId, ids, periodKey = getDailyVibePeriodKey()) {
  if (!userId || typeof window === 'undefined') return readDailyVibeMatchIds(userId, periodKey);
  const merged = readDailyVibeMatchIds(userId, periodKey);
  normalizeMatchConversationIds(ids).forEach((id) => {
    if (!merged.includes(id)) merged.push(id);
  });
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey) || {};
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({
        ...existing,
        matchConversationIds: merged,
        periodKey,
      }),
    );
  } catch {
    /* quota / private mode */
  }
  return merged;
}

/**
 * Persist the match conversation initiated after this period's daily check and
 * append it to today's id list. The cross-device copy is matchConversationIds
 * on the check doc, stamped server-side by initiateMyVvtfMatch.
 */
export function recordDailyVibeMatchFound(
  userId,
  { conversationId, foundAt = Date.now() },
  periodKey = getDailyVibePeriodKey(),
) {
  if (!userId || !conversationId || typeof window === 'undefined') return;
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey) || {};
    const matchConversationIds = normalizeMatchConversationIds(existing.matchConversationIds);
    if (!matchConversationIds.includes(conversationId)) matchConversationIds.push(conversationId);
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({
        ...existing,
        matchFoundConversationId: conversationId,
        matchFoundAt: foundAt,
        matchConversationIds,
        periodKey,
      }),
    );
  } catch {
    /* quota / private mode */
  }
}

export function writeDailyVibeChatCache(
  userId,
  { gifUrl, checkId = null, gifId = null, timestamp = Date.now() },
  periodKey = getDailyVibePeriodKey(),
) {
  if (!userId || !gifUrl || typeof window === 'undefined') return;
  try {
    const existing = readDailyVibePeriodCache(userId, periodKey) || {};
    window.localStorage.setItem(
      dailyVibeCacheStorageKey(userId, periodKey),
      JSON.stringify({
        ...existing,
        gifUrl,
        checkId,
        gifId,
        timestamp,
        periodKey,
      }),
    );
  } catch {
    /* quota / private mode */
  }
}

export function clearDailyVibeChatCache(userId, periodKey = getDailyVibePeriodKey()) {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(dailyVibeCacheStorageKey(userId, periodKey));
  } catch {
    /* ignore */
  }
}

/**
 * Persist a daily vibe check GIF for a signed-in user.
 * Stored at `dailyvibe/{userId}/checks/{checkId}` (one subcollection per user).
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 * @param {{ id?: string, title?: string, gifUrl?: string, fullUrl?: string, stillUrl?: string }} gif
 * @returns {Promise<{ checkId: string, newStreak: number|null, periodKey: string }|null>}
 */
export async function saveDailyVibeCheck(firestore, userId, gif) {
  if (!userId || !gif) return null;
  const gifUrl = gif.fullUrl || gif.gifUrl;
  if (!gifUrl) return null;

  const periodKey = getDailyVibePeriodKey();
  const alreadyRespondedThisPeriod = Boolean(
    await loadDailyVibeCheckForPeriod(firestore, userId, periodKey),
  );

  const docRef = await addDoc(collection(firestore, 'dailyvibe', userId, 'checks'), {
    userId,
    periodKey,
    gifUrl,
    fullUrl: gif.fullUrl || gif.gifUrl || null,
    stillUrl: gif.stillUrl || null,
    gifId: gif.id || null,
    title: gif.title || null,
    source: 'fox_daily_check',
    createdAt: serverTimestamp(),
  });

  writeDailyVibeChatCache(userId, {
    gifUrl,
    checkId: docRef.id,
    gifId: gif.id || null,
    timestamp: Date.now(),
  }, periodKey);

  if (!alreadyRespondedThisPeriod) {
    try {
      await incrementVibeAccuracyForDailyCheck(firestore, userId);
    } catch (err) {
      console.warn('Vibe accuracy increment failed:', err);
    }
  }

  let newStreak = null;
  try {
    newStreak = await updateVibeCheckStreakOnCompletion(firestore, userId);
  } catch (err) {
    console.warn('Vibe check streak update failed:', err);
    try {
      newStreak = await reconcileVibeCheckStreakFromDailyChecks(firestore, userId);
    } catch (reconcileErr) {
      console.warn('Vibe check streak reconcile failed:', reconcileErr);
    }
  }

  return { checkId: docRef.id, newStreak, periodKey };
}

/**
 * Repair `users/{uid}` streak fields when a daily-check GIF exists for today but the
 * counter still reads 0 (e.g. streak write failed after the GIF was saved/cached).
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 * @returns {Promise<number|null>}
 */
export async function reconcileVibeCheckStreakFromDailyChecks(firestore, userId) {
  if (!userId) return null;

  const current = getDailyVibePeriodKey();
  let currentCheck = await loadDailyVibeCheckForPeriod(firestore, userId, current);
  if (!currentCheck?.gifUrl && hasDailyVibeCheckForPeriod(userId, current)) {
    currentCheck = readDailyVibeChatCache(userId, current);
  }
  if (!currentCheck?.gifUrl) return null;

  const userRef = doc(firestore, 'users', userId);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return null;

  const data = snap.data();
  const lastPeriodKey = data.vibeCheckStreakLastPeriodKey || null;
  const storedStreak = typeof data.vibeCheckStreak === 'number' && Number.isFinite(data.vibeCheckStreak)
    ? data.vibeCheckStreak
    : 0;
  const display = resolveVibeCheckStreakDisplay(storedStreak, lastPeriodKey);

  if (lastPeriodKey === current && display >= 1) {
    return display;
  }

  return updateVibeCheckStreakOnCompletion(firestore, userId);
}

/**
 * Bump consecutive fox daily-check streak on `users/{uid}` (first response per period only).
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 */
export async function updateVibeCheckStreakOnCompletion(firestore, userId) {
  if (!userId) return null;

  const userRef = doc(firestore, 'users', userId);
  const snap = await getDoc(userRef);

  const periodKey = getDailyVibePeriodKey();
  const previousPeriodKey = getPreviousDailyVibePeriodKey();
  const data = snap.exists() ? snap.data() : {};
  const lastPeriodKey = data.vibeCheckStreakLastPeriodKey || null;
  const storedStreak = typeof data.vibeCheckStreak === 'number' && Number.isFinite(data.vibeCheckStreak)
    ? data.vibeCheckStreak
    : 0;

  // Already recorded for this period — idempotent (e.g. duplicate save or recovery).
  if (lastPeriodKey === periodKey) {
    // Repair: period key was written but streak stayed at 0 (failed/partial write).
    if (storedStreak < 1) {
      const repaired = 1;
      await setDoc(userRef, {
        vibeCheckStreak: repaired,
        vibeCheckStreakLastPeriodKey: periodKey,
        updatedAt: new Date(),
      }, { merge: true });
      return repaired;
    }
    return storedStreak;
  }

  const newStreak = lastPeriodKey === previousPeriodKey
    ? Math.min(VIBE_CHECK_STREAK_MAX, storedStreak + 1)
    : 1;

  await setDoc(userRef, {
    vibeCheckStreak: newStreak,
    vibeCheckStreakLastPeriodKey: periodKey,
    updatedAt: new Date(),
  }, { merge: true });

  return newStreak;
}

/**
 * On login, persist the streak's true value on `users/{uid}`.
 *
 * If the last fox daily-check landed today or yesterday, the stored streak still
 * stands. If the user missed yesterday (broke the chain), reset `vibeCheckStreak`
 * to 0 in Firestore so the stored value reflects reality.
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 * @returns {Promise<number|null>} streak after sync, or null when no user doc
 */
export async function syncVibeCheckStreakOnLogin(firestore, userId) {
  if (!userId) return null;

  const userRef = doc(firestore, 'users', userId);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return null;

  const data = snap.data();
  const lastPeriodKey = data.vibeCheckStreakLastPeriodKey || null;
  const storedStreak = typeof data.vibeCheckStreak === 'number' && Number.isFinite(data.vibeCheckStreak)
    ? data.vibeCheckStreak
    : 0;

  const current = getDailyVibePeriodKey();
  const previous = getPreviousDailyVibePeriodKey();
  const streakAlive = lastPeriodKey === current || lastPeriodKey === previous;

  // Sent a gif today or yesterday: streak stands as stored.
  if (streakAlive) return storedStreak;

  // Missed yesterday: reset, but only write when Firestore holds a stale value.
  if (storedStreak !== 0) {
    await updateDoc(userRef, {
      vibeCheckStreak: 0,
      updatedAt: new Date(),
    });
  }

  try {
    const reconciled = await reconcileVibeCheckStreakFromDailyChecks(firestore, userId);
    if (typeof reconciled === 'number' && reconciled > 0) {
      return reconciled;
    }
  } catch (err) {
    console.warn('Vibe check streak login reconcile failed:', err);
  }

  return 0;
}

/**
 * Latest daily-check GIF for the current 3am period, if any.
 *
 * The VVTF match stamp fields (`matchConversationId` / `matchUid` / `matchFoundAt` /
 * `matchConversationIds`) are aggregated across every check doc in the period, so a
 * duplicate check doc (cross-device GIF race) can't hide an earlier match or reset
 * the day's match count. Mirrors `periodMatchState` in functions/vvtfMatchInitiate.js.
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 * @param {string} [periodKey]
 */
export async function loadDailyVibeCheckForPeriod(
  firestore,
  userId,
  periodKey = getDailyVibePeriodKey(),
) {
  if (!userId || !periodKey) return null;

  const q = query(
    collection(firestore, 'dailyvibe', userId, 'checks'),
    where('periodKey', '==', periodKey),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;

  let latest = null;
  const matchConversationIds = [];
  let latestStamp = null;
  snap.docs.forEach((docSnap) => {
    const data = docSnap.data();
    const ts = data.createdAt?.toMillis?.() ?? 0;
    if (!latest || ts > latest._ts) {
      latest = { id: docSnap.id, ...data, _ts: ts };
    }
    normalizeMatchConversationIds([
      ...(Array.isArray(data.matchConversationIds) ? data.matchConversationIds : []),
      data.matchConversationId,
    ]).forEach((id) => {
      if (!matchConversationIds.includes(id)) matchConversationIds.push(id);
    });
    if (typeof data.matchConversationId === 'string' && data.matchConversationId) {
      const foundAt = typeof data.matchFoundAt === 'number' && Number.isFinite(data.matchFoundAt)
        ? data.matchFoundAt
        : 0;
      if (!latestStamp || foundAt >= latestStamp.matchFoundAt) {
        latestStamp = {
          matchConversationId: data.matchConversationId,
          matchUid: data.matchUid || null,
          matchFoundAt: foundAt,
        };
      }
    }
  });
  if (!latest) return null;
  delete latest._ts;
  if (latestStamp) {
    latest.matchConversationId = latestStamp.matchConversationId;
    latest.matchUid = latestStamp.matchUid;
    latest.matchFoundAt = latestStamp.matchFoundAt || latest.matchFoundAt || null;
  }
  latest.matchConversationIds = matchConversationIds;
  return latest;
}

/**
 * Daily-check GIFs for the current month, keyed by periodKey (latest per day).
 * Range covers the 1st of the current vibe-period month through today's period.
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 * @returns {Promise<Record<string, { gifUrl: string, stillUrl: string|null }>>}
 */
export async function loadDailyVibeChecksForMonth(firestore, userId, now = new Date()) {
  if (!userId) return {};

  const currentKey = getDailyVibePeriodKey(now);
  const monthStartKey = `${currentKey.slice(0, 8)}01`;

  const q = query(
    collection(firestore, 'dailyvibe', userId, 'checks'),
    where('periodKey', '>=', monthStartKey),
    where('periodKey', '<=', currentKey),
  );
  const snap = await getDocs(q);

  const byDay = {};
  snap.docs.forEach((docSnap) => {
    const data = docSnap.data();
    if (!data?.periodKey || !data?.gifUrl) return;
    const ts = data.createdAt?.toMillis?.() ?? 0;
    const existing = byDay[data.periodKey];
    if (!existing || ts > existing._ts) {
      byDay[data.periodKey] = {
        gifUrl: data.gifUrl,
        stillUrl: data.stillUrl || null,
        _ts: ts,
      };
    }
  });
  Object.values(byDay).forEach((entry) => delete entry._ts);
  return byDay;
}
